import React from 'react';
import DashboardPage from '../components/pages/Dashboard';
import { DirectoryPage } from '../components/directory/Directory';
import SlotsPage from '../components/directory/SlotsPage';
import OrganizationPage from '../components/pages/organizations/Organization';
import DashboardConfig from '../components/pages/admin/DashboardConfig';
import BillingSubscriptions from '../components/pages/admin/BillingSubscriptions';
import UserManagement from '../components/pages/admin/UserManagement';
import AdminLogin from '../components/pages/AdminLogin';
import PatientLoginPage from '../components/pages/PatientLogin';
import VerifyInvitePage from '../components/pages/VerifyInvite';
import SetupAccountPage from '../components/pages/SetupAccount';
import PatientDashboard from '../components/pages/PatientDashboard';
import ErrorPage from '../components/pages/Error';
import Billing from '../components/billing/BillingCheckout';
import BillingComplete from '../components/billing/BillingComplete';
import ApiKeyTab from '../components/pages/organizations/ApiKeyTab';
import SpecialtiesConfig from '../components/pages/admin/SpecialtiesConfig';
import DemoRedirect from '../components/pages/DemoRedirect';
import GeneralSettings from '../components/pages/admin/settings/GeneralSettings';
import Hl7LogsPage from 'src/components/pages/admin/Hl7LogsPage';
import LoginRedirect from '../components/pages/LoginRedirect';
import ZoomRedirect from '../components/pages/zoom/ZoomRedirect';
import DirectoryProvider from '../contexts/DirectoryContext';
import { VerificationProvider } from '../contexts/VerificationContext';
import { match } from 'path-to-regexp';

export interface RouteConfig {
  pattern: string;
  component: React.ComponentType<any>;
  requireAuth?: boolean;
  requireOrg?: boolean;
  requireOrgAdmin?: boolean;
  providers?: React.ComponentType<{ children: React.ReactNode }>[];
}

// Routes ordered by specificity: more specific routes (more segments, fewer params) first
// This ensures /:org_id/dashboard/:id matches before /:org_id/dashboard
export const routes: RouteConfig[] = [
  // Exact matches (no params)
  {
    pattern: '/',
    component: LoginRedirect,
    requireAuth: false,
    requireOrg: false,
  },
  {
    pattern: '/login',
    component: LoginRedirect,
    requireAuth: false,
    requireOrg: false,
  },
  {
    pattern: '/dashboard/demo',
    component: DemoRedirect,
    requireAuth: false,
    requireOrg: false,
  },
  {
    pattern: '/organizations',
    component: OrganizationPage,
    requireAuth: true,
    requireOrg: false,
  },
  {
    pattern: '/error',
    component: ErrorPage,
    requireAuth: false,
    requireOrg: false,
  },

  // More specific routes with params (more segments)
  {
    pattern: '/:org_id/login',
    component: AdminLogin,
    requireAuth: false,
    requireOrg: false,
  },
  {
    pattern: '/:org_id/patient_login',
    component: PatientLoginPage,
    requireAuth: false,
    requireOrg: false,
  },
  {
    pattern: '/:org_id/zoom/in-client/redirect',
    component: ZoomRedirect,
    requireAuth: false,
    requireOrg: false,
  },
  {
    pattern: '/:org_id/verify-invite',
    component: VerifyInvitePage,
    requireAuth: false,
    requireOrg: false,
  },
  {
    pattern: '/:org_id/setup-account',
    component: SetupAccountPage,
    requireAuth: false,
    requireOrg: false,
  },
  {
    pattern: '/:org_id/practitioner/search/:practitioner_role_id',
    component: SlotsPage,
    requireAuth: false,
    requireOrg: false,
    providers: [DirectoryProvider],
  },
  {
    pattern: '/:org_id/dashboard/:id',
    component: DashboardPage,
    requireAuth: true,
    requireOrg: true,
    providers: [VerificationProvider],
  },
  {
    pattern: '/:org_id/billing/checkout',
    component: Billing,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },
  {
    pattern: '/:org_id/billing/complete',
    component: BillingComplete,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },

  // Less specific routes (fewer segments)
  {
    pattern: '/:org_id/practitioner/search',
    component: DirectoryPage,
    requireAuth: false,
    requireOrg: false,
    providers: [DirectoryProvider],
  },
  {
    pattern: '/:org_id/dashboard-config',
    component: DashboardConfig,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },
  {
    pattern: '/:org_id/patient_dashboard',
    component: PatientDashboard,
    requireAuth: true,
    requireOrg: true,
  },
  {
    pattern: '/:org_id/billing',
    component: BillingSubscriptions,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },
  {
    pattern: '/:org_id/users',
    component: UserManagement,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },
  {
    pattern: '/:org_id/api-keys',
    component: ApiKeyTab,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },
  {
    pattern: '/:org_id/specialties',
    component: SpecialtiesConfig,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },
  {
    pattern: '/:org_id/general-settings',
    component: GeneralSettings,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },
  {
    pattern: '/:org_id/hl7-logs',
    component: Hl7LogsPage,
    requireAuth: true,
    requireOrg: true,
    requireOrgAdmin: true,
  },
];

// Pre-compile matchers for all routes with parameters for better performance
const routeMatchers = new Map<RouteConfig, ReturnType<typeof match>>();

// Initialize matchers for routes with parameters
routes.forEach(route => {
  if (route.pattern.includes(':')) {
    try {
      routeMatchers.set(route, match(route.pattern, { end: true }));
    } catch (error) {
      console.warn(`Failed to create matcher for route: ${route.pattern}`, error);
    }
  }
});

// Match a pathname against route patterns
export const matchRoute = (pathname: string): { route: RouteConfig; params: Record<string, string> } | null => {
  // Check exact matches first
  for (const route of routes) {
    if (route.pattern === pathname) {
      return { route, params: {} };
    }
  }

  // Check pattern matches (routes are ordered by specificity)
  for (const route of routes) {
    // Skip exact matches (already checked above)
    if (!route.pattern.includes(':')) {
      continue;
    }

    // Use path-to-regexp matcher to verify match and extract params
    const matcher = routeMatchers.get(route);
    if (!matcher) {
      continue;
    }

    const result = matcher(pathname);
    if (result) {
      // path-to-regexp returns { path, params } on match
      return {
        route,
        params: result.params as Record<string, string>
      };
    }
  }

  return null;
};
